import React, {useContext, useEffect, useState} from "react";
import {Box} from "@mui/material";
import Button from "@mui/material/Button/Button";
import Grid from "@mui/material/Grid";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import ReportFormsHandler from "./ReportForms/ReportFormsHandler";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {
    FormReportModel,
    IReportBarChart,
    IReportCompetitorsBenchmark,
    IReportDateCarGroup,
    IReportRegionLocationMonth,
    IReportRegionMonth,
    IReportUtilizationAndAvailability,
} from "../../api/types/report";
import BarChartReport from "./ReportComponents/BarChartReport/BarChartReport";
import RegionLocationMonthReport from "./ReportComponents/RegionLocationMonthReport/RegionLocationMonthReport";
import RegionMonthReport from "./ReportComponents/RegionMonthReport/RegionMonthReport";
import DateCarGroupReport from "./ReportComponents/DateCarGroupReport/DateCarGroupReport";
import {initialReportFormModel, reportValidationSchema,} from "./ReportBaseDefinition";
import {useReportUtils} from "./ReportForms/useReportUtils";
import CardContainer from "../../common/components/CardContainer";
import CompetitorsBenchmarkReport from "./ReportComponents/CompetitorsBenchmarkReport/CompetitorsBenchmarkReport";
import {EnumInterface, SelectElementModelBE, SelectElementModelFE} from "../../api/types/generic";
import {useRecoilState} from "recoil";
import {reportsAtom} from "../../state/reportRecoil";
import {fromEnumToFe} from "../../utils";
import {FormAutocomplete} from "../../common/form-fields";
import {useTypesRepository} from "../../api/repositories/TypesRepository";
import Loading from "../../common/components/Loading";
import {PRICING_REPORTS} from "../../constants";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import UtilizationAndAvailabilityReport
    from "./ReportComponents/UtilizationAndAvailabilityReport/UtilizationAndAvailabilityReport";

const Report = () => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    const [result, setResult] = useState<
        | IReportBarChart[]
        | IReportRegionLocationMonth[]
        | IReportRegionMonth[]
        | IReportCompetitorsBenchmark[]
        | IReportDateCarGroup[]
        | IReportUtilizationAndAvailability
        | null
    >(null);

    //USE RECOIL ATOM
    const [reportsList, setReportsList] = useRecoilState(reportsAtom);

    //USE CUSTOM HOOK
    const {getReportsType} = useTypesRepository();

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    //setto tutti i dealers options
    const dealerOption: SelectElementModelFE[] = user.dealers?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    const dealerFilteredOptions = dealerOption
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const getStaticContents = async () => {
        try {
            const reportsType: EnumInterface = await getReportsType();
            const reportsList: SelectElementModelFE[] = fromEnumToFe(reportsType);
            setReportsList(reportsList);
        } catch (e) {
        }
    };

    // Use Effect
    useEffect(() => {
        getStaticContents();
        // eslint-disable-next-line
    }, []);

    const steps = [
        t("pricing:report.steps.choose"),
        t("pricing:report.steps.configure"),
        t("pricing:report.steps.report"),
    ];

    const form = useForm<FormReportModel>({
        defaultValues: initialReportFormModel(legalEntityOptions, dealerOption),
        resolver: yupResolver(
            reportValidationSchema(t),
            {},
            {
                rawValues: true,
            }
        ),
    });

    //START STEP MANAGMENT
    const [activeStep, setActiveStep] = useState(0);

    // Use Alert Utils
    const {triggerFormFields, createPostRequest} = useReportUtils(
        activeStep,
        setActiveStep,
        form
    );

    const handleNext = async () => {
        try {
            const triggerFields = await triggerFormFields();
            if (activeStep === 1 && Boolean(triggerFields)) {
                const result = await createPostRequest(everyLegalEntitiesWithoutAll, dealerFilteredOptions);

                if (result) {
                    setActiveStep((prevActiveStep: number) => prevActiveStep + 1);
                    setResult(result);
                }
            }
        } catch (e) {
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const renderReportsForm = () => {
        switch (form.watch("type")?.label) {
            case PRICING_REPORTS.RPD_INCOMING_RESERVATIONS:
                return <BarChartReport result={result}/>;
            case PRICING_REPORTS.COMPETITORS_BENCHMARK:
                return (
                    <CompetitorsBenchmarkReport
                        result={result as IReportCompetitorsBenchmark[]}
                    />
                );
            case PRICING_REPORTS.RESERVATIONS_NUMBER:
                return <RegionLocationMonthReport result={result}/>;
            case PRICING_REPORTS.RESERVATIONS_PER_DAY:
                return (
                    <RegionMonthReport
                        result={result}
                        withCurrency={false}
                        divided={false}
                    />
                );
            case PRICING_REPORTS.AVERAGE_RPD:
                return (
                    <RegionMonthReport
                        result={result}
                        withCurrency={false}
                        divided={true}
                    />
                );
            case PRICING_REPORTS.REVENUES:
                return (
                    <RegionMonthReport
                        result={result}
                        withCurrency={true}
                        divided={true}
                    />
                );
            case PRICING_REPORTS.RESERVATIONS_BOOKING_DATE_AND_CARGROUPS:
                return <DateCarGroupReport result={result}/>;
            case PRICING_REPORTS.UTILIZATION_AVAILABILITY:
                return (
                    <UtilizationAndAvailabilityReport
                        result={result as IReportUtilizationAndAvailability}
                        currentMonthSelected={
                            form.getValues().utilizationAndAvailability.currentMonthSelected
                        }
                    />
                );
            default:
                break;
        }
    };

    return (
        <Box>
            <SectionHeader
                title={t("translation:auths.SCOPE_PRC_VIEW_REPORT.name")}
            />
            <Grid container spacing={2} p={2}>
                {/* STEPPER */}
                <Grid item xs={12} sm={12}>
                    <Stepper
                        activeStep={activeStep}
                        sx={{width: "100%", marginBottom: "20px"}}
                    >
                        {steps.map((label) => {
                            const stepProps: {
                                completed?: boolean;
                                disabled?: boolean;
                            } = {};
                            const labelProps: {
                                optional?: React.ReactNode;
                            } = {};
                            return (
                                <Step
                                    key={label}
                                    {...stepProps}
                                    style={{
                                        width: `calc(100% / ${steps.length})`,
                                    }}
                                >
                                    <StepLabel {...labelProps}>{label}</StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>
                </Grid>
                {/* FORMS */}

                <CardContainer>
                    <>
                        <Grid item xs={12}>
                            {" "}
                            <FormProvider {...form}>
                                {/* {activeStep < 3 && ( */}
                                <Grid item xs={12} md={3} mt={2}>
                                    <FormAutocomplete
                                        {...form.register("type")}
                                        label={
                                            activeStep === 0
                                                ? `${t("pricing:report.labels.reportType")}*`
                                                : t("pricing:report.labels.reportSelected")
                                        }
                                        options={reportsList}
                                        isOptionEqualToValue={(
                                            option: SelectElementModelFE,
                                            value: SelectElementModelFE
                                        ) => option.code === value.code}
                                        readOnly={activeStep > 0}
                                        handleChange={(value: SelectElementModelFE) => {
                                            form.reset({
                                                ...initialReportFormModel,
                                                type: value,
                                            });
                                        }}
                                    />
                                </Grid>
                                {/* )} */}
                                {activeStep < 2 ? (
                                    <ReportFormsHandler activeStep={activeStep}/>
                                ) : (
                                    <Grid item xs={12}>
                                        {renderReportsForm()}
                                    </Grid>
                                )}
                            </FormProvider>
                        </Grid>

                        {/* BUTTONS */}
                        <Grid
                            item
                            xs={12}
                            display={"flex"}
                            justifyContent={"flex-end"}
                            mt={3}
                        >
                            {activeStep > 0 && (
                                <Button color="inherit" onClick={handleBack} sx={{mr: 1}}>
                                    {t("pricing:alert.buttons.back")}
                                </Button>
                            )}
                            {activeStep < 2 && (
                                <Button onClick={handleNext}>
                                    {t("pricing:alert.buttons.next")}
                                </Button>
                            )}
                        </Grid>
                    </>
                </CardContainer>
            </Grid>

            <Loading/>
        </Box>
    );
};

export default Report;
